import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Await, matchPath, Outlet, redirect, useAsyncValue, useLoaderData, useLocation, useNavigate } from "react-router-dom";

import { MenuListItems } from "./menuListItems";
import AppNavigation from "router/AppNavigation";
import { Button, Autocomplete, TextField } from '@mui/material';
import { CMAccountListItem, GetCampaignsResponseItem, CurrentUserDetails } from "services/apiService";
import axiosInstance from "services/axiosInstance";
import CircularProgress from '@mui/material/CircularProgress';
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Swal from "sweetalert2";

const drawerWidth: number = 240;

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const theme_menulist = createTheme({
    palette: {
        mode: "dark",
        primary: { main: 'rgb(102, 157, 246)' },
        background: { paper: 'rgb(5, 30, 52)' },
    },
});


// ================================= Error functions =================================
function ErrorComponent() {
    React.useEffect(() => {
        Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Something went wrong ! Please log out and try again.",
        });
    }, []);

    return (
        <p style={{ color: "red" }}>
            Error while loading CM Account. Please log out first.
        </p>
    );
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    "& .MuiDrawer-paper": {
        position: "relative",
        whiteSpace: "nowrap",
        width: drawerWidth,
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        boxSizing: "border-box",
        ...(!open && {
            overflowX: "hidden",
            transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            width: theme.spacing(7),
            [theme.breakpoints.up("sm")]: {
                width: theme.spacing(9),
            },
        }),
    },
}));

const Root = () => {
    // Ensure user is logged in
    const navigate = useNavigate();
    useEffect(() => {
        if (!localStorage.getItem("access_token")) {
            navigate(AppNavigation.Login);
        }
    }, [navigate]);

    // Drawer state
    const [open, setOpen] = useState(true);
    const toggleDrawer = () => {
        setOpen(!open);
    };

    // Update toolbar title when location changes
    const [toolbarTitle, setToolbarTitle] = useState("KTK SMS");
    const location = useLocation();

    const loaderData = useLoaderData() as {
        data: Promise<[CurrentUserDetails, CMAccountListItem[]]>;
    };

    console.log("loaderData : ", loaderData)

    useEffect(() => {
        if (matchPath(AppNavigation.Campaigns, location.pathname)) {
            setToolbarTitle("Campaigns");
        } else if (matchPath(AppNavigation.CreateCampaign, location.pathname)) {
            setToolbarTitle("Create Campaign");
        } else if (matchPath(AppNavigation.EditCampaign, location.pathname)) {
            setToolbarTitle("Edit Campaign");
        } else if (
            matchPath(AppNavigation.CampaignAddressBook, location.pathname)
        ) {
            setToolbarTitle("Campaign Address Book");
        } else if (matchPath(AppNavigation.CampaignSendTest, location.pathname)) {
            setToolbarTitle("Send Test");
        } else if (matchPath(AppNavigation.AddressBooks, location.pathname)) {
            setToolbarTitle("Address Books");
        } else if (matchPath(AppNavigation.AddressBookDetail, location.pathname)) {
            setToolbarTitle("Address Book Details");
        } else if (matchPath(AppNavigation.DwhRequests, location.pathname)) {
            setToolbarTitle("DWH Segments");
        } else if (matchPath(AppNavigation.Settings, location.pathname)) {
            setToolbarTitle("Settings");
        } else {
            setToolbarTitle("KTK SMS");
        }
    }, [location]);

    return (
        <Box sx={{ display: "flex" }}>
            <CssBaseline />
            <AppBar position="absolute" open={open}>
                <Toolbar
                    sx={{
                        pr: "24px",
                    }}
                >
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        onClick={toggleDrawer}
                        sx={{
                            marginRight: "36px",
                            ...(open && { display: "none" }),
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography
                        component="h1"
                        variant="h6"
                        color="inherit"
                        noWrap
                        sx={{ flexGrow: 1 }}
                    >
                        {toolbarTitle}
                    </Typography>
                    <React.Suspense
                        fallback={
                            <Box
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <CircularProgress />
                                {/* <p>Loading CM Account...</p> */}
                            </Box>
                        }
                    >
                        <Await
                            resolve={loaderData.data}
                            errorElement={<ErrorComponent />}
                        // <p style={{ color: "red" }}>Error while loading CM Account . Please log out first</p>

                        // }
                        >

                            <CmAccount />
                        </Await>
                    </React.Suspense>

                    <Button
                        onClick={() => {
                            localStorage.clear()
                            window.location.reload()
                        }}
                        color="inherit"
                    >Logout</Button>
                </Toolbar>
            </AppBar>
            <Drawer variant="permanent" open={open}>
                <Toolbar
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        px: [1],
                    }}
                >
                    <IconButton onClick={toggleDrawer}>
                        <ChevronLeftIcon />
                    </IconButton>
                </Toolbar>
                <Divider />

                <React.Suspense
                    fallback={
                        <Box
                            sx={{
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <CircularProgress />
                            <p>Loading User details...</p>
                        </Box>
                    }
                >
                    <Await
                        resolve={loaderData.data}
                        errorElement={<p>Error while loading user details</p>}
                    >

                        <Box sx={{ display: 'flex' }}>
                            <List component="nav"><MenuListItems /></List>
                        </Box>
                    </Await>
                </React.Suspense>

            </Drawer>
            <Box
                component="main"
                sx={{
                    backgroundColor: (theme) =>
                        theme.palette.mode === "light"
                            ? theme.palette.grey[100]
                            : theme.palette.grey[900],
                    flexGrow: 1,
                    height: "100vh",
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Toolbar />
                <Container
                    maxWidth={false}
                    sx={{
                        mt: 4,
                        mb: 4,
                        flexGrow: 1,
                    }}
                >
                    <Outlet />
                </Container>
            </Box>
        </Box>
    );
};

export const CmAccount = () => {
    const [cmAccounts, currentUserDetails] = useAsyncValue() as [
        GetCampaignsResponseItem[],
        CurrentUserDetails
    ]
    const [dataFilter, setDataFilter] = useState();
    const cmAccountList = useMemo(
        () => cmAccounts,
        [cmAccounts]
    )
    const default_cm_account = cmAccountList.find((cmAccount) => cmAccount.id === currentUserDetails.default_cm_account)
    const [cmAccount, setCmAccount] = React.useState(default_cm_account || cmAccounts[0])


    const onCmAccountChange = async (e: any, value: any, cm_account_id: any, user_id: any) => {
        e.preventDefault()
        setCmAccount(value)
        navigate("/", {
            state: { campagnesFiltrees: dataFilter }
        });
    }

    const navigate = useNavigate();
    // useEffect(() => {
    //     let payload = new FormData();

    //     console.log("\t # User details :", currentUserDetails.id)
    //     payload.append("default_cm_account", cmAccount.id.toString())

    //     payload.append("cm_account_id", cmAccount.account_id);
    //     // switchDefaultCmAccount(payload)
    //     const response = axiosInstance.patch(
    //         (process.env.REACT_APP_API_UPDATE_USER_ENDPOINT as string).replace(":userId", currentUserDetails.id.toString()),
    //         payload
    //     ).then((res) => {
    //         console.log("\t # Response success")
    //         // redirect('/campaigns')

    //         // Construire l'URL correcte
    //         const campaignsUrl = (process.env.REACT_APP_API_GET_FILTER_CAMPAIGNS_ENDPOINT as string)
    //             .replace(":user_id", currentUserDetails.id.toString())
    //             .replace(":cm_id", cmAccount.account_id.toString());

    //         console.log("\t 🔗 API Endpoint :", campaignsUrl);

    //         // Ensuite, récupérer les campagnes filtrées
    //         axiosInstance
    //             .get(campaignsUrl)
    //             .then((response) => {
    //                 console.log("🚀 Campagnes filtrées :", response.data);
    //                 // navigate("/", {replace: true});
    //                 navigate("/", {
    //                     replace: true,
    //                     state: { campagnesFiltrees: response.data }
    //                 });
    //             })
    //             .catch((error) => {
    //                 console.error("❌ Erreur lors de la récupération des campagnes:", error);
    //             });
    //     });

    //     // // Redirect to home
    //     // navigate('/', { replace: true });
    // }, [cmAccount])



    useEffect(() => {
        const updateUserDetails = async () => {
            try {
                let payload = new FormData();
                console.log("\t # User details :", currentUserDetails.id);
                payload.append("default_cm_account", cmAccount.id.toString());
                payload.append("cm_account_id", cmAccount.account_id);

                const response = await axiosInstance.patch(
                    (process.env.REACT_APP_API_UPDATE_USER_ENDPOINT as string).replace(":userId", currentUserDetails.id.toString()),
                    payload
                );
                console.log("\t # Response success");

                const campaignsUrl = (process.env.REACT_APP_API_GET_FILTER_CAMPAIGNS_ENDPOINT as string)
                    .replace(":user_id", currentUserDetails.id.toString())
                    .replace(":cm_id", cmAccount.account_id.toString());
                console.log("\t 🔗 API Endpoint :", campaignsUrl);

                // Ensuite, récupérer les campagnes filtrées
                const campaignResponse = await axiosInstance.get(campaignsUrl);
                console.log("🚀 Campagnes filtrées :", campaignResponse.data);
                setDataFilter(campaignResponse.data);
                // Naviguer après avoir récupéré les campagnes

            } catch (error) {
                console.error("❌ Erreur lors de la récupération des campagnes:", error);
            }
            // navigate("/",{replace: true});
        };

        updateUserDetails();
    }, [cmAccount]);





    return (
        // <Button color="inherit">CmAccount</Button>
        <Autocomplete
            disablePortal
            id="cm-account"
            options={cmAccountList}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name}
            renderOption={(props, option) => <li {...props} key={option.id}>
                {option.name}
            </li>}
            value={cmAccount}
            // onChange={(e, value) => { setCmAccount(value) }}
            onChange={(e, value) => { onCmAccountChange(e, value, cmAccount?.id, currentUserDetails.id) }}
            renderInput={(params) => <TextField {...params} label="Cm Account" />}
            sx={{ width: 300 }}
            size="small"
            disableClearable
        />

    )
}

export default Root;
